/**
 * IntegrationsPanel — QuickBooks + Sage + Mail
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  loadQBStatus,
  saveSettings,
  type CompanySettings,
} from '../../../api/settings'
import {
  startQBConnect,
  disconnectQB,
  fetchQBSyncErrors,
  syncProjectToQB,
} from '../../../api/quickbooks'
import McpIntegrationsPanel from './McpIntegrationsPanel'

interface Props {
  /** Optional project id for manual recovery sync from settings context */
  projectId?: number
}

export default function IntegrationsPanel({ projectId }: Props) {
  const qc = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const { data: qb, refetch: refetchQb } = useQuery({
    queryKey: ['qb-status'],
    queryFn: loadQBStatus,
    staleTime: 30_000,
    retry: 1,
  })

  const { data: syncErrors = [] } = useQuery({
    queryKey: ['qb-sync-errors'],
    queryFn: fetchQBSyncErrors,
    enabled: !!qb?.connected,
    staleTime: 15_000,
  })

  async function handleConnect() {
    setErr(null)
    setBusy('connect')
    try {
      const url = await startQBConnect()
      window.location.href = url
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function handleDisconnect() {
    if (!window.confirm('Disconnect QuickBooks? Sync mappings will be cleared.')) return
    setBusy('disconnect')
    setErr(null)
    try {
      await disconnectQB()
      await refetchQb()
      qc.invalidateQueries({ queryKey: ['qb-sync-errors'] })
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function handleAutoSyncToggle() {
    const next = qb?.qb_auto_sync === false
    setBusy('autosync')
    try {
      await saveSettings({ qb_preferences: { qb_auto_sync: next } } as Partial<CompanySettings>)
      await refetchQb()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function handleManualProjectSync() {
    if (!projectId) return
    setBusy('sync')
    setErr(null)
    try {
      await syncProjectToQB(projectId)
      qc.invalidateQueries({ queryKey: ['qb-sync-errors'] })
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  const envLabel = qb?.sandbox || qb?.environment === 'sandbox' ? 'Sandbox' : 'Production'

  return (
    <>
      <div id="quickbooks" className="panel" data-testid="panel-quickbooks" style={{ marginTop: 14 }}>
        <div className="panel-h">
          <div className="panel-tt">QuickBooks Online</div>
          <span className="panel-st">Sync projects, invoices, and AR automatically</span>
        </div>
        <div className="int-row">
          <div className="int-ic" style={{ background: '#0f0f0f', color: '#2ca01c' }}>qb</div>
          <div>
            <div className="int-tt">
              {qb?.needs_reconnect
                ? 'Reconnect required'
                : qb?.connected
                  ? `Connected · ${envLabel}`
                  : 'Not connected'}
            </div>
            <div className="int-st">
              {qb?.needs_reconnect
                ? (qb.last_error || 'QuickBooks authorization expired — reconnect to resume auto-sync.')
                : qb?.connected
                  ? `${qb.company_name ?? 'QuickBooks'}${qb.last_sync_at ? ` · last sync ${new Date(qb.last_sync_at).toLocaleDateString()}` : ''}`
                  : 'Connect once — pay apps and payments sync without extra steps.'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {qb?.connected ? (
              <>
                {qb.needs_reconnect ? (
                  <button type="button" className="btn b-dk btn-sm" data-testid="qb-reconnect-btn"
                    disabled={busy === 'connect'} onClick={handleConnect}>
                    Reconnect QuickBooks
                  </button>
                ) : null}
                <button type="button" className="btn b-red btn-sm" data-testid="qb-disconnect-btn"
                  disabled={!!busy} onClick={handleDisconnect}>
                  Disconnect
                </button>
              </>
            ) : (
              <button type="button" className="btn b-dk btn-sm" data-testid="qb-connect-btn"
                disabled={busy === 'connect'} onClick={handleConnect}>
                Connect QuickBooks →
              </button>
            )}
          </div>
        </div>

        {qb?.connected && !qb.needs_reconnect && (
          <>
            <div className="set-row" style={{ padding: '11px 16px' }}>
              <div className="set-row-l">
                <div className="set-row-tt">Auto-sync</div>
                <div className="set-row-st">Submit pay apps and record payments → QuickBooks automatically</div>
              </div>
              <div className="set-row-r">
                <button type="button" className={`btn btn-sm ${qb.qb_auto_sync !== false ? 'b-dk' : 'b-ol'}`}
                  data-testid="qb-autosync-toggle" disabled={busy === 'autosync'} onClick={handleAutoSyncToggle}>
                  {qb.qb_auto_sync !== false ? 'On' : 'Off'}
                </button>
              </div>
            </div>
            {projectId != null && (
              <div className="set-row" style={{ padding: '11px 16px' }}>
                <div className="set-row-l">
                  <div className="set-row-tt">Manual sync</div>
                  <div className="set-row-st">Recovery — re-push this project&apos;s submitted pay apps</div>
                </div>
                <div className="set-row-r">
                  <button type="button" className="btn b-ol btn-sm" data-testid="qb-manual-sync-btn"
                    disabled={busy === 'sync'} onClick={handleManualProjectSync}>
                    Sync to QuickBooks
                  </button>
                </div>
              </div>
            )}
            {syncErrors.length > 0 && (
              <div style={{ padding: '10px 16px 14px', fontSize: 11, color: 'var(--ink3)' }} data-testid="qb-sync-errors">
                <strong style={{ color: 'var(--red)' }}>{syncErrors.length} sync issue{syncErrors.length === 1 ? '' : 's'} — </strong>
                {syncErrors.slice(0, 3).map((e) => (
                  <span key={`${e.local_entity_type}-${e.local_entity_id}`} style={{ marginRight: 8 }}>
                    {e.local_entity_type} #{e.local_entity_id}: {e.last_error}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
        {err && (
          <div style={{ padding: '8px 16px 12px', fontSize: 11, color: 'var(--red)' }}>{err}</div>
        )}
      </div>

      <McpIntegrationsPanel />

      {/* Sage — unchanged placeholder */}
      <div id="sage" className="panel" data-testid="panel-sage" style={{ marginTop: 14 }}>
        <div className="panel-h">
          <div className="panel-tt">Sage</div>
          <span className="panel-st">Sage Intacct &amp; Sage CM</span>
        </div>
        <div className="int-row">
          <div className="int-ic" style={{ background: '#00b388' }}>sg</div>
          <div>
            <div className="int-tt">Sage integration</div>
            <div className="int-st">Coming soon · Developer Partner setup in progress · supports Intacct (REST) and CM (REST+SOAP)</div>
          </div>
          <div>
            <button type="button" className="btn b-ol btn-sm" disabled style={{ opacity: .5, cursor: 'not-allowed' }}
              data-testid="sage-coming-soon-btn">
              Coming soon
            </button>
          </div>
        </div>
      </div>

      {/* Mail Services */}
      <div id="mail" className="panel" data-testid="panel-mail" style={{ marginTop: 14 }}>
        <div className="panel-h">
          <div className="panel-tt">Mail services</div>
          <span className="panel-st">Lob.com for certified mail · Resend for transactional email</span>
        </div>
        <div className="int-row">
          <div className="int-ic" style={{ background: '#000' }}>Lb</div>
          <div>
            <div className="int-tt">Lob.com · certified mail</div>
            <div className="int-st">Mail Pre Lien notices and lien documents · $8.99 per piece</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <span className="chip c-grn">Active</span>
            <button type="button" className="btn b-ol btn-sm" data-testid="lob-manage-btn" disabled style={{ opacity: .5 }}>
              Manage
            </button>
          </div>
        </div>
        <div className="int-row">
          <div className="int-ic" style={{ background: '#000' }}>Rs</div>
          <div>
            <div className="int-tt">Resend · transactional email</div>
            <div className="int-st">Invites, daily digest, reminders</div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <span className="chip c-grn">Active</span>
            <button type="button" className="btn b-ol btn-sm" data-testid="resend-manage-btn" disabled style={{ opacity: .5 }}>
              Manage
            </button>
          </div>
        </div>
      </div>
    </>
  )
}
